import { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useEffectOnActive } from 'keepalive-for-react';
import { ShareNetwork, Heart, BookOpen } from '@phosphor-icons/react';
import notify, { notifyIcons } from '../utils/notify';
import ContactFooter from '../components/ContactFooter';
import PlaceCard from '../components/PlaceCard';
import PlaceCardSkeleton from '../components/PlaceCardSkeleton';
import EmptyState from '../components/EmptyState';
import { plural, PLACES } from '../utils/plural';
import { getCollectionBySlug, addToSavedCollections, removeFromSavedCollections } from '../services/api';
import telegramService from '../services/telegram';
import { useAuth } from '../context/AuthContext';
import { useCollections } from '../context/CollectionsContext';

const CollectionPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  // Признак «сохранено» выводится из контекста, а не хранится своей копией.
  // Раньше было локальное состояние плюс запрос `checkIsSavedCollection`
  // в useEffect по [collection, isAuthenticated] — при возврате на ту же
  // подборку ни одна зависимость не менялась, страница лежала в LRU-кеше,
  // и кнопка показывала «Сохранена» после удаления в «Избранном». Хуже:
  // нажатие шло удалять запись, которой уже нет, и падало с ошибкой
  const { savedCollections, addSavedCollection, removeSavedCollection, loadSaved } = useCollections();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const savedRecord = useMemo(
    () => savedCollections.find(sc => sc.collection?.id === collection?.id) || null,
    [savedCollections, collection]
  );
  const isSaved = Boolean(savedRecord);

  const getImageUrl = (imageData) => {
    if (!imageData) return 'https://via.placeholder.com/800x600?text=No+Image';
    const url = imageData.url || imageData.data?.url || imageData;
    if (!url) return 'https://via.placeholder.com/800x600?text=No+Image';
    return url.startsWith('http') ? url : url;
  };

  useEffect(() => {
    loadCollection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Тихое обновление при возврате: правки с этого устройства уже в контексте,
  // но список мог измениться с другого. background не даёт подменить
  // показанное лоадером
  useEffectOnActive((active) => {
    if (active && isAuthenticated) loadSaved({ background: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, loadSaved]);

  const loadCollection = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getCollectionBySlug(slug);

      if (response.data && response.data.length > 0) {
        const collectionData = response.data[0];
        setCollection(collectionData);
      } else {
        setError('Подборка не найдена');
      }
    } catch (err) {
      setError('Ошибка загрузки данных');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    telegramService.hapticFeedback('light');
    navigate(-1);
  };

  const handleSaveCollection = async () => {
    if (!isAuthenticated) {
      notify.error('Войдите, чтобы сохранить подборку');
      return;
    }

    telegramService.hapticFeedback('medium');

    try {
      if (savedRecord) {
        await removeFromSavedCollections(savedRecord.id);
        // Список правится на месте, а не перезагружается: сервер операцию
        // уже подтвердил, и счётчик в профиле меняется сразу.
        // `isSaved` пересчитается сам — он выведен из этого списка
        removeSavedCollection(savedRecord.id);
        notify('Убрано из сохранённых', { icon: notifyIcons.favoriteRemoved });
      } else {
        const response = await addToSavedCollections(collection.id);
        // Ответ create не populate-ит связь, а признак «сохранено» ищется
        // по `sc.collection.id` — без неё кнопка осталась бы «Сохранить»
        addSavedCollection({ ...response.data, collection });
        notify('Подборка сохранена', { icon: notifyIcons.favoriteAdded });
      }
    } catch (error) {
      // 409 «уже сохранена» и 404 «записи нет» означают, что список в контексте
      // разошёлся с сервером — перечитываем, вместо того чтобы сообщать
      // об ошибке действия, которое по факту уже в нужном состоянии
      const status = error.response?.status;
      if (status === 409 || status === 404) {
        loadSaved({ background: true });
        notify('Список обновлён', { icon: notifyIcons.info });
      } else {
        notify.error('Не удалось сохранить. Проверьте связь');
      }
    }
  };

  const handleShare = () => {
    telegramService.hapticFeedback('light');
    const shareUrl = `https://t.me/spbneformal_app_bot/apps?startapp=collection-${collection.slug}`;
    const shareText = `Посмотри эту подборку: ${collection.title} 📍`;
    const telegramShareUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    telegramService.openTelegramLink(telegramShareUrl);
  };

  // Скелетоны вместо спиннера: каркас держит ту же разметку, в которую
  // страница развернётся, и экран не схлопывается в точку по центру
  if (loading) {
    return (
      <div className="min-h-screen bg-theme-bg pb-6">
        <div
          className="px-4 pb-6"
          style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
        >
          <div className="animate-shimmer mb-4 h-56 w-full rounded-lg" />
          <div className="h-7 w-2/3 rounded-sm bg-theme-card-secondary" />
          <div className="mt-2 h-4 w-1/4 rounded-sm bg-theme-card-secondary" />
        </div>
        <div className="grid grid-cols-2 gap-4 px-4">
          {[0, 1, 2, 3].map(i => <PlaceCardSkeleton key={i} />)}
        </div>
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme-bg px-4">
        <div className="text-center">
          <p className="mb-6 text-body text-theme-text-secondary">{error || 'Подборка не найдена'}</p>
          <button
            onClick={handleBack}
            className="h-12 rounded-md border-0 bg-theme-card-secondary px-[18px] text-label text-action-text transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            Назад
          </button>
        </div>
      </div>
    );
  }

  const placesCount = collection.places?.length || 0;

  return (
    <div className="min-h-screen bg-theme-bg pb-6">
      {/* Заголовка «Подборка» над названием больше нет: он повторял
          очевидное и отнимал уровень у самого названия */}
      <div
        className="px-4 pb-6"
        style={{ paddingTop: 'calc(var(--safe-area-top) + var(--tg-header-height) + 16px)' }}
      >
        {collection.image && (
          <div className="mb-4 h-56 w-full overflow-hidden rounded-lg">
            <img
              src={getImageUrl(collection.image)}
              alt={collection.title}
              className="h-full w-full object-cover"
            />
          </div>
        )}

        <h1 className="text-h1 text-theme-text-primary">{collection.title}</h1>

        {/* Число мест видно сразу, а не после прокрутки до конца ленты */}
        {placesCount > 0 && (
          <p className="tabular mt-1 text-caption text-theme-text-secondary">
            {placesCount} {plural(placesCount, PLACES)}
          </p>
        )}

        {collection.description && (
          <p className="mt-3 whitespace-pre-line text-body text-theme-text-secondary">
            {collection.description}
          </p>
        )}

        {/* Обе кнопки контурные, в один ряд — брендбук 6.3. Сохранённое
            состояние не может быть ярче исходного действия, а «Поделиться»
            рядом с ним делало из шапки две синие полосы подряд */}
        <div className="mt-5 flex gap-2">
          {/* Сердце, как в плашке подборки и на карточке места: один глиф
              на сохранение везде. Красным только залитое — сердце серым
              не читается как избранное (брендбук 8.3) */}
          <button
            onClick={handleSaveCollection}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-0 bg-theme-card-secondary text-label text-action-text transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Heart
              size={20}
              weight={isSaved ? 'fill' : 'regular'}
              className={isSaved ? 'text-theme-favorite-text' : undefined}
            />
            {isSaved ? 'Сохранена' : 'Сохранить'}
          </button>

          <button
            onClick={handleShare}
            className="flex h-12 flex-1 items-center justify-center gap-2 rounded-md border-0 bg-theme-card-secondary text-label text-action-text transition-colors duration-fast ease-out active:scale-98"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <ShareNetwork size={20} />
            Поделиться
          </button>
        </div>
      </div>

      {/* Обычная карточка места и сетка в две колонки — как на главной,
          в поиске и избранном. Своя горизонтальная карточка с описанием
          и кнопкой была четвёртым видом карточки места в проекте.
          Описание уехало на страницу места: здесь оно шло обычным <p>,
          хотя в базе размечено markdown — на экране были видны звёздочки */}
      <div className="px-4">
        {collection.places?.length > 0 ? (
          <div className="grid grid-cols-2 gap-4">
            {collection.places.map(place => (
              <PlaceCard key={place.id} place={place} />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={BookOpen}
            title="В подборке пока нет мест"
            description="Мы наполним её в ближайшее время"
          />
        )}
      </div>

      <div className="px-4">
        <ContactFooter />
      </div>
    </div>
  );
};

export default CollectionPage;
